"use client";

import { useCallback, useTransition, memo } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";

import { getJobsAction, searchJobsAction } from "@/actions/job/job.action";
import { HeadingText } from "@/components/atoms/HeadingText/heading-text";
import { ItemNotFound } from "@/components/molecules/NotFound";
import LoadingGear from "@/components/molecules/Loader/loading";
import { JobBoardList } from "./list";
import { JobListFilterForm } from "./filter-form";
import { PaginationContainer } from "../PaginationContainer/pagination-container";

interface Filters {
  categories?: string[];
  experienceLevel?: string[];
  skills?: string[];
  type?: string[];
  location?: { latitude: number; longitude: number, country?: string, state?: string, city?: string };
}

type ArrayFilterKey = keyof Pick<Filters, "categories" | "experienceLevel" | "skills" | "type">;

// nuqs parseAsArrayOf (default) serializes arrays as one query value joined by "," (URLSearchParams.set).
// getAll then returns e.g. ["id1,id2"] instead of ["id1", "id2"]. Split to restore real arrays.
const NUQS_ARRAY_SEPARATOR = ",";

// Helper: Extracts filters from URLSearchParams (excluding 'page')
const getFiltersFromParams = (params: URLSearchParams): Filters => {
  const result: Filters = {};

  // Handle array parameters
  const arrayParams: Record<ArrayFilterKey, string> = {
    categories: "roless[]",
    experienceLevel: "experienceLevel[]",
    skills: "skills[]",
    type: "type[]",
  };

  (Object.entries(arrayParams) as [ArrayFilterKey, string][]).forEach(([key, param]) => {
    const values = params
      .getAll(param)
      .filter(Boolean)
      .flatMap((v) => v.split(NUQS_ARRAY_SEPARATOR).map((s) => s.trim()).filter(Boolean));
    if (values.length > 0) result[key] = values;
  });

  // Handle location
  if (params.has('latitude') && params.has('longitude')) {
    result.location = {
      latitude: Number(params.get('latitude')),
      longitude: Number(params.get('longitude')),
      country: params.get('country') || '',
      state: params.get('state') || '',
      city: params.get('city') || '',
    };
  }

  return result;
};


// Helper: Checks if any filter is set (excluding page)
const hasAnyFilter = (filters: Filters) =>
  !!(
    (filters.categories && filters.categories.length > 0) ||
    (filters.experienceLevel && filters.experienceLevel.length > 0) ||
    (filters.skills && filters.skills.length > 0) ||
    (filters.type && filters.type.length > 0) ||
    filters.location
  );

function JobBoardComponent({ translations }: { translations: Record<string, any> }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  // Extract page, default to 1
  const currentPage = Number(searchParams.get("page")) || 1;

  // Extract filters from params (excluding page)
  const filters = getFiltersFromParams(searchParams);

  // Query to fetch jobs
  const jobsQuery: any = useQuery({
    queryKey: ["jobs", searchParams.toString()],
    queryFn: () => {
      if (hasAnyFilter(filters)) {
        // Filters present: use searchJobsAction
        return searchJobsAction({ filters, page: currentPage });
      } else {
        // No filters: use getJobsAction (pagination only)
        return getJobsAction(currentPage);
      }
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const jobs = jobsQuery.data?.data?.data?.items ?? [];
  const totalPages = jobsQuery.data?.data?.data?.totalPages ?? 1;
  const isLoading = jobsQuery.isLoading;
  const isError = jobsQuery.isError;
  const isEmpty = !isLoading && !isError && jobs.length === 0;

  // Handles filter changes: updates URL, resets page to 1
  const handleFilterChange = useCallback(
    (newFilters: Partial<Filters>) => {
      const params = new URLSearchParams();

      newFilters.categories?.forEach((c) => params.append("roless[]", c));
      newFilters.experienceLevel?.forEach((e) => params.append("experienceLevel[]", e));
      newFilters.skills?.forEach((s) => params.append("skills[]", s));
      newFilters.type?.forEach((t) => params.append("type[]", t));

      if (newFilters.location) {
        params.set("latitude", String(newFilters.location.latitude));
        params.set("longitude", String(newFilters.location.longitude));
        params.set("country", String(newFilters.location.country));
        params.set("state", String(newFilters.location.state));
        params.set("city", String(newFilters.location.city));
      }

      // Always reset page to 1 on filter change
      params.set("page", "1");

      startTransition(() => {
        router.replace(`${pathname}?${params.toString()}`);
      });
    },
    [router, pathname]
  );

  // Prepare queryParams for PaginationContainer
  const queryParams: Record<string, string | string[]> = {};

  searchParams.forEach((value, key) => {
    if (queryParams[key]) {
      queryParams[key] = Array.isArray(queryParams[key])
        ? [...queryParams[key] as string[], value]
        : [queryParams[key] as string, value];
    } else {
      queryParams[key] = value;
    }
  });

  return (
    <section className="w-full overflow-hidden dark:bg-gray-900">
      <div className="mx-auto w-full max-w-screen-2xl p-4 md:px-12 md:pt-4">
        <div className="container duration-500">
          <div className="flex flex-wrap gap-12 pt-16 md:gap-24">
            <div className="flex flex-1 flex-col">
              <HeadingText level={2}>{translations?.latestJobs}</HeadingText>
              <div className="py-10 flex flex-col gap-4">
                <MainContent
                  jobs={jobs}
                  totalPages={totalPages}
                  isLoading={isLoading || isPending}
                  isError={isError}
                  isEmpty={isEmpty}
                  queryParams={queryParams}
                />
              </div>
            </div>
            <div className="sticky top-[156px] mx-auto max-w-screen-sm">
              <JobListFilterForm
                filters={filters}
                setFilters={handleFilterChange}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const MainContent = memo(
  ({
    jobs,
    totalPages,
    isLoading,
    isError,
    isEmpty,
    queryParams,
  }: {
    jobs: any[];
    totalPages: number;
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
    queryParams: Record<string, string | string[]>;
  }) => {
    if (isLoading) {
      return <LoadingGear />;
    }

    if (isError || isEmpty) {
      return <ItemNotFound />;
    }

    return (
      <>
        <JobBoardList jobBoardList={jobs} />
        {totalPages > 1 && (
          <PaginationContainer
            totalPages={totalPages}
            queryParams={queryParams}
          />
        )}
      </>
    );
  }
);

MainContent.displayName = "MainContent";

export const JobBoard = ({ translations }: { translations: Record<string, any> }) => <JobBoardComponent translations={translations} />;
